/**
 * ModerationBadge — "under review" marker on content only its author can see.
 *
 * Held content is filtered out server-side for every other viewer, so this
 * badge is always self-addressed and needs no ownership check of its own.
 *
 * It is deliberately non-interactive. The short label carries the state and a
 * `sr-only` sentence carries the full explanation, following the same split as
 * `components/ui/Badge`. A button opening a dialog would have to render that
 * dialog inside a post header, a comment row or a chat bubble — all inline
 * contexts, and two of them already inside a modal — which breaks both HTML
 * nesting and the parent modal's focus trap.
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  isHeldForReview,
  moderationHoldMessage,
  type ModeratedContentType,
} from '@wlb/shared/api/moderation';
import { ReviewShieldIcon } from '../../components/ui/icons';
import './moderation.css';

interface ModerationBadgeProps {
  /** Raw status straight off the domain model; anything but a hold renders nothing. */
  moderationStatus?: unknown;
  contentType: ModeratedContentType;
  /** `chip` carries the visible label, `icon` suits dense rows like a chat bubble. */
  variant?: 'chip' | 'icon';
  className?: string;
}

export function ModerationBadge({
  moderationStatus,
  contentType,
  variant = 'chip',
  className = '',
}: ModerationBadgeProps) {
  const { t } = useTranslation();

  if (!isHeldForReview(moderationStatus)) {
    return null;
  }

  const explanation = moderationHoldMessage(t, contentType);
  const classes = ['moderation-badge', `moderation-badge--${variant}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <span className={classes} title={explanation} aria-hidden>
        <ReviewShieldIcon size={variant === 'icon' ? 13 : 14} />
        {variant === 'chip' && (
          <span className="moderation-badge__label">{t('moderation.badge')}</span>
        )}
      </span>
      <span className="sr-only">{explanation}</span>
    </>
  );
}
